import { atom, selector, selectorFamily, useRecoilCallback } from 'recoil';
import { APP_CONFIG } from '../../constants/APP_CONFIG';
import { FeatureToggle, SDUser } from '../../models/SDUser';
import { oAuthService } from '../../services/oAuthService';
import { sdLegacySessionService } from '../../services/sdLegacySessionService';
import { cacheAtomEffect } from '../recoilHelpers/cacheAtomEffect';

export class UserNotLoggedInError {}

export const canDebugStuffState = selector({
  key: 'sd:canDebugStuff',
  get: o => !['uat', 'prod'].includes(APP_CONFIG.build.environment) || !!o.get(authenticatedUserState)?.admin
});

export const authTktState = atom<string | undefined>({
  key: 'sd:authTkt',
  default: undefined,
  effects_UNSTABLE: [cacheAtomEffect('localStorage')]
});

export const sessionIdState = atom<string | undefined>({
  key: 'sd:sessionId',
  default: undefined,
  effects_UNSTABLE: [cacheAtomEffect('localStorage')]
});

export const authVerifiedState = selector({
  key: 'sd:authVerified',
  get: async o => {
    const authenticatedUser = o.get(authenticatedUserState);
    return authenticatedUser
      ? await oAuthService
          .verifyAuthWithServer()
          .then(() => true)
          .catch(() => false)
      : false;
  }
});

/**
 * Gets or sets whether the `authenticatedUser` user is an admin.
 */
export const isAdminState = selector({
  key: 'sd:isAdmin',
  get: s => s.get(authenticatedUserState)?.admin || false
});

/**
 * Gets or sets whether the `authenticatedUser` user is an employee.
 */
export const isEmployeeState = selector({
  key: 'sd:isEmployee',
  get: s => s.get(authenticatedUserState)?.employee || false
});

/**
 * Gets or sets the logged in user.
 *
 * NOTE: This is usually not what you want to use
 * @see currentUserState which gets the current user, cannot be undefined, and supports impersonation
 */
export const authenticatedUserState = atom<SDUser | undefined>({
  key: 'sd:authenticatedUser',
  default: undefined,
  effects_UNSTABLE: [cacheAtomEffect('localStorage')]
});

/**
 * The user that is being impersonated or undefined if not impersonating
 */
export const impersonatedUserState = selector<SDUser | undefined>({
  key: 'sd:impersonatedUser',
  get: ({ get }) => get(_impersonatedUserState)
});

/**
 * @deprecated @see impersonatedUserState
 * Do not export this
 */
const _impersonatedUserState = atom<SDUser | undefined>({
  key: 'sd:_impersonatedUser',
  default: undefined,
  effects_UNSTABLE: [cacheAtomEffect('localStorage')]
});

/**
 * Sets the user that is being impersonated or undefined if not impersonating
 */
export const useSetImpersonatedUserState = () =>
  useRecoilCallback(o => {
    return async (username: string | undefined) => {
      const sessionId = await o.snapshot.getPromise(sessionIdState);
      if (!sessionId) return;
      o.set(sellerNumberOverridesState, undefined);
      if (username) {
        const userToImpersonate = await oAuthService.getUserDetails(username);
        if (userToImpersonate.employee) throw new Error('Impersonation failed: Cannot impersonate an employee user');
        o.set(_impersonatedUserState, userToImpersonate);
      } else {
        o.set(_impersonatedUserState, undefined);
      }
      await sdLegacySessionService.impersonate(sessionId, username);
    };
  }, []);

/**
 * Gets the impersonator. When not impersonating, returns undefined.
 */
export const impersonatorUserState = selector({
  key: 'sd:impersonatorUser',
  get: ({ get }) => (get(impersonatedUserState) ? get(authenticatedUserState) : undefined)
});

/**
 * Returns user the app is operating as.
 *
 * If impersonating, then the impersonated user is returned
 *
 * If not impersonating, the authenticated user is returned
 *
 * To access the impersonator, @see impersonatorUserState
 */
export const currentUserState = selector<SDUser | undefined>({
  key: 'sd:currentUser',
  get: ({ get }) => {
    const loggedInUser = get(authenticatedUserState);
    const impersonatedUser = get(impersonatedUserState);
    const sellerNumberOverrides = get(sellerNumberOverridesState);
    const theUser = impersonatedUser || loggedInUser;
    return theUser && sellerNumberOverrides
      ? {
          ...theUser,
          sellerNumbers: sellerNumberOverrides,
          accounts: [
            {
              guid: 'N/A',
              name: 'N/A',
              sellerNumbers: sellerNumberOverrides
            }
          ]
        }
      : theUser;
  }
});

/**
 * Get whether a beta toggle is enabled for the current user
 */
export const betaToggleState = selectorFamily<boolean, FeatureToggle>({
  key: 'sd:betaToggleState',
  get:
    name =>
    ({ get }) => {
      const user = get(currentUserState);
      return user?.betaUserFeatures?.includes(name) ?? false;
    }
});

export const csrfTokenState = atom<string | undefined>({
  key: 'sd:csrfToken',
  default: undefined,
  effects_UNSTABLE: [cacheAtomEffect('localStorage')]
});

/**
 * Returns true if the user logged in
 */
export const isLoggedInState = selector({
  key: 'sd:isLoggedIn',
  get: ({ get }) => {
    const csrf = get(authenticatedUserState);
    return !!csrf;
  }
});

/**
 * When set, overrides (restricts) the accounts a user has access to with only the seller numbers given.
 */
export const sellerNumberOverridesState = atom<number[] | undefined>({
  key: 'sd:sellerNumberOverrides',
  default: undefined,
  effects_UNSTABLE: [cacheAtomEffect('localStorage')]
});

/**
 * Set the account ID that a user is currently impersonating. Should only be set when sellerNumberOverrides are also set.
 */
export const accountGroupIdState = atom<string | undefined>({
  key: 'sd:accountGroupId',
  default: undefined,
  effects_UNSTABLE: [cacheAtomEffect('localStorage')]
});
